<template>
  <div class="purchase-list-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Header: Breadcrumb, Title, Subtitle & Action Buttons -->
    <div class="page-header-wrap mb-4">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <!-- Left: Breadcrumb + Title + Subtitle -->
        <div class="header-text-block">
          <PageBreadcrumb :items="[{ text: 'Purchase List' }]" class="mb-1" />
          <div class="d-flex flex-wrap align-center gap-2">
            <p class="page-subtitle mb-0">Manage all your purchase invoices in one place.</p>
            <span class="thankam-balance-pill" title="Your own recovered thankam, available to use when settling a purchase">
              <v-icon size="13" color="#9a6b27" class="mr-1">mdi-gold</v-icon>
              Thankam Balance: {{ Number(jewelleryThankamBalance).toFixed(2) }} gm
            </span>
          </div>
        </div>

        <!-- Right: Header Action Buttons -->
        <div class="header-actions-group d-flex flex-wrap align-center mt-3 mt-sm-0">
          <!-- + New Purchase (Amber button) -->
          <v-btn
            depressed
            class="btn-new-purchase mr-2"
            @click="$router.push('/Purchase')"
          >
            <v-icon left size="16">mdi-plus</v-icon>
            New Purchase
          </v-btn>

          <!-- PDF button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="downloadPDF()"
          >
            <v-icon left size="16" class="btn-icon">mdi-file-document-outline</v-icon>
            PDF
          </v-btn>

          <!-- Billing List button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="$router.push('/SalesList')"
          >
            <v-icon left size="16" class="btn-icon">mdi-format-list-bulleted</v-icon>
            Billing List
          </v-btn>

          <!-- Summary button -->
          <v-btn
            outlined
            depressed
            class="btn-action-outline mr-2"
            @click="summaryDialog = true"
          >
            <v-icon left size="16" class="btn-icon">mdi-poll</v-icon>
            Summary
          </v-btn>

          <!-- More options menu button (Three dots) -->
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                depressed
                class="btn-action-outline btn-icon-only"
                v-bind="attrs"
                v-on="on"
                title="More Options"
              >
                <v-icon size="18" color="#64748b">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="rounded-lg shadow-sm">
              <v-list-item @click="getData()">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="#9a6b27">mdi-refresh</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-size-13">Refresh List</v-list-item-title>
              </v-list-item>
              <v-list-item @click="resetFilters()">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="#64748b">mdi-filter-remove-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="font-size-13">Clear Filters</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="filter-card-bar mb-4">
      <div class="d-flex flex-wrap align-center filter-row">
        <!-- From Date -->
        <div class="filter-item date-item mr-2">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="fromDate ? formatDate(fromDate) : ''"
                placeholder="From Date"
                background-color="#ffffff"
                outlined
                color="#9a6b27"
                readonly
                hide-details
                clearable
                class="filter-input-field"
                dense
                prepend-inner-icon="mdi-calendar-range-outline"
                v-bind="attrs"
                v-on="on"
                @click:clear="fromDate = ''"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menu.save(fromDate)"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- To Date -->
        <div class="filter-item date-item mr-2">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="toDate ? formatDate(toDate) : ''"
                placeholder="To Date"
                background-color="#ffffff"
                outlined
                color="#9a6b27"
                readonly
                hide-details
                clearable
                class="filter-input-field"
                dense
                prepend-inner-icon="mdi-calendar-range-outline"
                v-bind="attrs"
                v-on="on"
                @click:clear="toDate = ''"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menu2.save(toDate)"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- Keyword Search -->
        <div class="filter-item search-item mr-2 flex-grow-1">
          <v-text-field
            v-model="keyword"
            background-color="#ffffff"
            clearable
            hide-details
            color="#9a6b27"
            placeholder="Search bill no., supplier, invoice..."
            outlined
            class="filter-input-field"
            dense
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="getData()"
          ></v-text-field>
        </div>

        <!-- Status Select -->
        <div class="filter-item status-item mr-2">
          <v-select
            v-model="status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            placeholder="All"
            outlined
            hide-details
            dense
            background-color="#ffffff"
            class="filter-input-field status-select"
            color="#9a6b27"
          ></v-select>
        </div>

        <!-- Search Button -->
        <v-btn
          depressed
          class="btn-search-amber mr-2"
          @click="getData()"
        >
          <v-icon left size="16">mdi-magnify</v-icon>
          Search
        </v-btn>

        <!-- Reset Button -->
        <v-btn
          outlined
          depressed
          class="btn-reset-outline"
          @click="resetFilters()"
        >
          <v-icon left size="16" class="mr-1">mdi-refresh</v-icon>
          Reset
        </v-btn>
      </div>
    </div>

    <!-- Main Table Card -->
    <v-card class="table-card rounded-2xl" elevation="0">
      <!-- Table Content with Precise Column Alignment -->
      <div class="table-responsive">
        <table class="custom-table">
          <thead>
            <tr>
              <th class="col-sno text-left pl-4" @click="sortBy('sno')">
                <span>#</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-date text-left" @click="sortBy('purchaseDate')">
                <span>PURCHASE DATE</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-bill text-left" @click="sortBy('billNo')">
                <span>BILL NO</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-supplier text-left" @click="sortBy('supplierName')">
                <span>SUPPLIER NAME</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-inv-no text-left" @click="sortBy('invoiceNo')">
                <span>INVOICE NO</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-inv-date text-left" @click="sortBy('invoiceDate')">
                <span>INVOICE DATE</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-weight text-center" @click="sortBy('totalNetWeight')">
                <span>TOTAL WT (GM)</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-status text-center" @click="sortBy('settleStatus')">
                <span>STATUS</span>
                <v-icon x-small class="sort-icon">mdi-unfold-more-horizontal</v-icon>
              </th>
              <th class="col-actions text-center pr-4">ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in displayedList"
              :key="value._id || i"
              class="table-row"
            >
              <!-- S.NO -->
              <td class="col-sno text-left pl-4 cell-sno">
                {{ (page - 1) * itemsPerPage + i + 1 }}
              </td>

              <!-- Purchase Date -->
              <td class="col-date text-left">
                <span v-if="value.purchaseDate" class="date-text">{{ formatDate(value.purchaseDate) }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Bill No -->
              <td class="col-bill text-left">
                <span v-if="value.billNo" class="bill-pill">
                  {{ (value.billPrefix || '') + value.billNo }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Supplier Name -->
              <td class="col-supplier text-left">
                <span v-if="value.supplierId" class="supplier-text">{{ value.supplierId.supplierName }}</span>
                <span v-else-if="value.supplierName" class="supplier-text">{{ value.supplierName }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Invoice No -->
              <td class="col-inv-no text-left">
                <span v-if="value.invoiceNo" class="inv-text">{{ value.invoiceNo }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Invoice Date -->
              <td class="col-inv-date text-left">
                <span v-if="value.invoiceDate" class="inv-date-text">{{ formatDate(value.invoiceDate) }}</span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Total Wt(Gm) (Bold, numerical only without 'gm' suffix to match design) -->
              <td class="col-weight text-center">
                <span v-if="value.totalNetWeight !== undefined && value.totalNetWeight !== null" class="weight-val">
                  {{ Number(value.totalNetWeight).toLocaleString() }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Status (Soft pill with dot) -->
              <td class="col-status text-center">
                <span
                  v-if="value.settleStatus === 'Settled'"
                  class="status-pill status-settled"
                >
                  <span class="status-dot dot-settled"></span>
                  Settled
                </span>
                <span
                  v-else-if="value.settleStatus === 'notSettled'"
                  class="status-pill status-not-settled"
                >
                  <span class="status-dot dot-not-settled"></span>
                  Not Settled
                </span>
                <span
                  v-else-if="value.settleStatus === 'partiallySettled'"
                  class="status-pill status-partially-settled"
                >
                  <span class="status-dot dot-partially-settled"></span>
                  Partially Settled
                </span>
                <span v-else class="text-muted">-</span>
              </td>

              <!-- Action: Eye Button + Dots Button -->
              <td class="col-actions text-center pr-4">
                <div class="d-flex align-center justify-center">
                  <button
                    type="button"
                    class="table-action-btn mr-2"
                    title="View Details"
                    @click="$router.push('/purchaseDetails?id=' + value._id)"
                  >
                    <v-icon size="16" color="#64748b">mdi-eye-outline</v-icon>
                  </button>

                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="table-action-btn"
                        v-bind="attrs"
                        v-on="on"
                        title="More Actions"
                      >
                        <v-icon size="16" color="#64748b">mdi-dots-vertical</v-icon>
                      </button>
                    </template>
                    <v-list dense class="rounded-lg shadow-sm">
                      <v-list-item @click="$router.push('/purchaseDetails?id=' + value._id)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#9a6b27">mdi-eye-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-size-13">View Details</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="$router.push('/PurchaseSettlement2?id=' + value._id)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#9a6b27">mdi-file-document-edit-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="font-size-13">Settlement Details</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>

            <!-- No Data row -->
            <tr v-if="!displayedList || displayedList.length === 0">
              <td colspan="9" class="text-center py-8 text-muted">
                <v-icon medium color="#9ca3af" class="mb-2">mdi-package-variant</v-icon>
                <div style="font-size: 13px; font-weight: 500;">No purchase records found</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Footer / Pagination -->
      <div class="table-footer d-flex flex-wrap align-center justify-space-between py-3 px-4">
        <!-- Records Counter Text -->
        <div class="footer-records-text">
          Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRecordsCount }} records
        </div>

        <!-- Pagination Controls -->
        <div class="footer-pagination-wrap d-flex align-center">
          <button
            type="button"
            class="pagination-btn pagination-arrow mr-1"
            :disabled="page <= 1"
            @click="page > 1 ? page-- : null"
            title="Previous Page"
          >
            ‹
          </button>

          <button
            v-for="p in visiblePages"
            :key="p"
            type="button"
            class="pagination-btn pagination-page-num mr-1"
            :class="{ 'pagination-active': page === p }"
            @click="page = p"
          >
            {{ p }}
          </button>

          <button
            type="button"
            class="pagination-btn pagination-arrow mr-3"
            :disabled="page >= totalPages"
            @click="page < totalPages ? page++ : null"
            title="Next Page"
          >
            ›
          </button>

          <!-- Dropdown for Items Per Page -->
          <div class="page-size-select-wrap">
            <v-select
              v-model="itemsPerPage"
              :items="pageSizeOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              color="#9a6b27"
              class="page-size-select"
            ></v-select>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Summary View Modal -->
    <v-dialog v-model="summaryDialog" max-width="500px">
      <v-card class="rounded-xl pa-4">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <div class="dialog-icon-badge mr-2">
              <v-icon color="#9a6b27" size="20">mdi-poll</v-icon>
            </div>
            <h3 class="font-weight-bold" style="color: #1e293b; font-size: 18px;">Purchase Summary</h3>
          </div>
          <v-btn icon small @click="summaryDialog = false">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-4" style="border-color: #f1ece5;"></v-divider>

        <v-layout wrap>
          <v-flex xs6 pr-2 pb-3>
            <div class="summary-metric-card">
              <span class="metric-label">Total Purchases</span>
              <span class="metric-value">{{ totalRecordsCount }}</span>
            </div>
          </v-flex>
          <v-flex xs6 pl-2 pb-3>
            <div class="summary-metric-card">
              <span class="metric-label">Total Net Weight</span>
              <span class="metric-value">{{ totalWeightSum }} gm</span>
            </div>
          </v-flex>
          <v-flex xs6 pr-2>
            <div class="summary-metric-card status-settled-bg">
              <span class="metric-label text-green">Settled</span>
              <span class="metric-value text-green">{{ settledCount }}</span>
            </div>
          </v-flex>
          <v-flex xs6 pl-2>
            <div class="summary-metric-card status-not-settled-bg">
              <span class="metric-label text-red">Not Settled</span>
              <span class="metric-value text-red">{{ notSettledCount }}</span>
            </div>
          </v-flex>
        </v-layout>

        <div class="text-right mt-4">
          <v-btn color="#9a6b27" dark depressed class="rounded-lg text-capitalize px-5 font-weight-bold" @click="summaryDialog = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      itemsPerPage: 10,
      pageSizeOptions: [
        { text: "10 / page", value: 10 },
        { text: "20 / page", value: 20 },
        { text: "50 / page", value: 50 },
        { text: "100 / page", value: 100 },
      ],
      menu: false,
      menu2: false,
      Pagelength: 1,
      totalRecords: 0,
      PurchasedList: [],
      keyword: "",
      fromDate: "",
      toDate: "",
      statusOptions: [
        { text: "All", value: "" },
        { text: "Settled", value: "Settled" },
        { text: "Not Settled", value: "Not Settled" },
        { text: "Partially Settled", value: "Partially Settled" },
      ],
      settlestatus: "",
      status: "",
      summaryDialog: false,
      sortColumn: null,
      sortAsc: true,
      jewelleryThankamBalance: 0,
    };
  },
  watch: {
    page() {
      this.getData();
    },
    itemsPerPage() {
      this.page = 1;
      this.getData();
    },
    status() {
      if (this.status === "Settled") {
        this.settlestatus = "Settled";
      } else if (this.status === "Not Settled") {
        this.settlestatus = "notSettled";
      } else if (this.status === "Partially Settled") {
        this.settlestatus = "partiallySettled";
      } else {
        this.settlestatus = "";
      }
      this.page = 1;
      this.getData();
    },
    fromDate() {
      this.page = 1;
      this.getData();
    },
    toDate() {
      this.page = 1;
      this.getData();
    },
  },
  mounted() {
    this.getData();
    this.getJewelleryThankamBalance();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalRecordsCount() {
      return this.totalRecords || (this.PurchasedList ? this.PurchasedList.length : 0);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecordsCount / this.itemsPerPage));
    },
    paginationStart() {
      if (this.totalRecordsCount === 0) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      return Math.min(this.page * this.itemsPerPage, this.totalRecordsCount);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.page;
      const pages = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) {
        pages.push(i);
      }
      return pages.length ? pages : [1];
    },
    displayedList() {
      if (!this.PurchasedList) return [];
      let list = [...this.PurchasedList];
      if (this.sortColumn) {
        list.sort((a, b) => {
          let valA = a[this.sortColumn];
          let valB = b[this.sortColumn];
          if (this.sortColumn === "supplierName") {
            valA = a.supplierId ? a.supplierId.supplierName : a.supplierName || "";
            valB = b.supplierId ? b.supplierId.supplierName : b.supplierName || "";
          }
          if (valA < valB) return this.sortAsc ? -1 : 1;
          if (valA > valB) return this.sortAsc ? 1 : -1;
          return 0;
        });
      }
      return list;
    },
    totalWeightSum() {
      if (!this.PurchasedList) return 0;
      return this.PurchasedList.reduce(
        (sum, item) => sum + (Number(item.totalNetWeight) || 0),
        0
      ).toFixed(2);
    },
    settledCount() {
      if (!this.PurchasedList) return 0;
      return this.PurchasedList.filter((item) => item.settleStatus === "Settled")
        .length;
    },
    notSettledCount() {
      if (!this.PurchasedList) return 0;
      return this.PurchasedList.filter((item) => item.settleStatus === "notSettled")
        .length;
    },
  },
  methods: {
    getJewelleryThankamBalance() {
      axios({
        method: "GET",
        url: "/oldgold/acidifiedthankam/balance",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.jewelleryThankamBalance = response.data.acidifiedThankam || 0;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    sortBy(col) {
      if (this.sortColumn === col) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortColumn = col;
        this.sortAsc = true;
      }
    },
    resetFilters() {
      this.fromDate = "";
      this.toDate = "";
      this.keyword = "";
      this.status = "";
      this.settlestatus = "";
      this.page = 1;
      this.getData();
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/purchaseitem/getalllist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          limit: this.itemsPerPage,
          keyword: this.keyword,
          from: this.fromDate,
          to: this.toDate,
          settlestatus: this.settlestatus,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.PurchasedList = response.data.data;
            this.totalRecords =
              response.data.totalLength ||
              response.data.count ||
              (response.data.data ? response.data.data.length : 0);
            this.Pagelength = Math.ceil(this.totalRecords / this.itemsPerPage);
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    downloadPDF() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/purchasereport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          from: this.fromDate,
          to: this.toDate,
          keyword: this.keyword,
          settlestatus: this.settlestatus,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "PurchaseReport.pdf");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return item;
      const day = dt.getDate();
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@400;500;600;700&display=swap");

.purchase-list-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #1e293b;
  padding-bottom: 24px;
}

/* Page Header */
.page-header-wrap {
  padding: 2px 0;
}

.breadcrumbs-row {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.breadcrumb-sep {
  color: #94a3b8;
  margin: 0 6px;
  font-weight: 300;
}

.breadcrumb-item {
  color: #64748b;
}

.page-main-title {
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 25px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  margin: 0;
  letter-spacing: -0.025em;
}

.page-subtitle {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
  letter-spacing: -0.01em;
}

.gap-2 {
  gap: 8px;
}

.thankam-balance-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 700;
  color: #9a6b27;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  padding: 3px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}

/* Header Buttons */
.btn-new-purchase {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-new-purchase:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.btn-action-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  transition: all 0.2s ease !important;
}

.btn-action-outline .btn-icon {
  color: #64748b !important;
  transition: color 0.2s ease;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
  transform: translateY(-1px);
}

.btn-action-outline:hover .btn-icon {
  color: #9a6b27 !important;
}

.btn-icon-only {
  min-width: 38px !important;
  width: 38px !important;
  padding: 0 !important;
}

/* Filter Card / Bar */
.filter-card-bar {
  background-color: transparent;
}

.filter-row {
  gap: 8px;
}

.date-item {
  width: 145px;
  flex-shrink: 0;
}

.search-item {
  min-width: 220px;
}

.status-item {
  width: 130px;
  flex-shrink: 0;
}

/* Filter Inputs */
.filter-input-field >>> .v-input__control {
  min-height: 38px !important;
}

.filter-input-field >>> .v-input__slot {
  min-height: 38px !important;
  height: 38px !important;
  font-size: 13px !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  background-color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
}

.filter-input-field >>> input {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  letter-spacing: -0.01em !important;
}

.filter-input-field >>> fieldset {
  border-color: #e2e8f0 !important;
}

.filter-input-field:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.filter-input-field.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.filter-input-field >>> .v-text-field__details {
  display: none !important;
}

.filter-input-field >>> .v-input__append-inner,
.filter-input-field >>> .v-input__prepend-inner {
  margin-top: 7px !important;
}

.filter-input-field >>> .v-icon {
  font-size: 18px !important;
  color: #94a3b8 !important;
}

.status-select >>> .v-select__selection {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  color: #334151 !important;
  font-weight: 500 !important;
  letter-spacing: -0.01em !important;
}

.btn-search-amber {
  height: 38px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  padding: 0 18px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-search-amber:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.btn-reset-outline {
  height: 38px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
  color: #334151 !important;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  letter-spacing: -0.01em !important;
  transition: all 0.2s ease !important;
}

.btn-reset-outline:hover {
  border-color: #cbd5e1 !important;
  background-color: #f8fafc !important;
  color: #0f172a !important;
}

/* Main Table Card */
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

/* Custom Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #ffffff;
}

.custom-table th {
  padding: 13px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #f1f5f9;
  user-select: none;
  cursor: pointer;
  line-height: 1.3;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
}

.custom-table th:hover {
  color: #1e293b;
}

.sort-icon {
  color: #94a3b8 !important;
  margin-left: 2px;
  vertical-align: middle;
}

.custom-table td {
  padding: 13px 14px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
  white-space: nowrap;
  vertical-align: middle;
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
}

.table-row {
  transition: background-color 0.12s ease;
}

.table-row:hover {
  background-color: #fafaf9;
}

/* Column Widths */
.col-sno {
  width: 48px;
  min-width: 44px;
}

.col-date {
  width: 120px;
  min-width: 110px;
}

.col-bill {
  width: 105px;
  min-width: 95px;
}

.col-supplier {
  min-width: 150px;
}

.col-inv-no {
  width: 105px;
  min-width: 90px;
}

.col-inv-date {
  width: 120px;
  min-width: 110px;
}

.col-weight {
  width: 120px;
  min-width: 110px;
}

.col-status {
  width: 130px;
  min-width: 120px;
}

.col-actions {
  width: 95px;
  min-width: 90px;
}

/* Cell Typography & Badges */
.cell-sno {
  color: #1e293b;
  font-weight: 600;
  font-size: 13px;
}

.date-text {
  font-weight: 500;
  color: #334151;
  font-size: 13px;
}

.bill-pill {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  background-color: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  display: inline-block;
}

.supplier-text {
  text-transform: capitalize;
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.inv-text {
  color: #475569;
  font-weight: 500;
  font-size: 13px;
}

.inv-date-text {
  color: #475569;
  font-weight: 500;
  font-size: 13px;
}

.weight-val {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
  font-feature-settings: "tnum";
}

/* Status Dot Badges (Matching Image 1) */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  gap: 6px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.status-settled {
  background-color: #dcfce7;
  color: #16a34a;
}

.dot-settled {
  background-color: #16a34a;
}

.status-not-settled {
  background-color: #fee2e2;
  color: #ef4444;
}

.dot-not-settled {
  background-color: #ef4444;
}

.status-partially-settled {
  background-color: #fef3c7;
  color: #d97706;
}

.dot-partially-settled {
  background-color: #d97706;
}

/* Action Icon Buttons */
.table-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.table-action-btn:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

/* Table Footer & Pagination */
.table-footer {
  background-color: #ffffff;
  border-top: 1px solid #f1f5f9;
}

.footer-records-text {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.pagination-btn {
  height: 32px;
  min-width: 32px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  color: #334151;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.pagination-btn:hover:not(:disabled):not(.pagination-active) {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.pagination-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pagination-arrow {
  font-size: 16px;
  line-height: 1;
}

.pagination-active {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important; background-color: #9a6b27 !important;
  border-color: #9a6b27 !important;
  color: #ffffff !important;
}

.page-size-select-wrap {
  width: 105px;
}

.page-size-select >>> .v-input__control {
  min-height: 32px !important;
}

.page-size-select >>> .v-input__slot {
  min-height: 32px !important;
  height: 32px !important;
  font-size: 12px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  background-color: #ffffff !important;
}

.page-size-select >>> .v-select__selection {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #334151 !important;
}

.page-size-select >>> .v-text-field__details {
  display: none !important;
}

/* Summary Modal */
.dialog-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #fef7ee;
  border: 1px solid #fde8d0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-metric-card {
  padding: 14px;
  border-radius: 10px;
  background-color: #fcfbf9;
  border: 1px solid #f1ece5;
  display: flex;
  flex-direction: column;
}

.metric-label {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 2px;
}

.metric-value {
  font-size: 17px;
  font-weight: 800;
  color: #1a1626;
}

.status-settled-bg {
  background-color: #f0fdf4 !important;
  border-color: #bbf7d0 !important;
}

.status-not-settled-bg {
  background-color: #fef2f2 !important;
  border-color: #fecaca !important;
}

.text-green {
  color: #15803d !important;
}

.text-red {
  color: #dc2626 !important;
}

.text-muted {
  color: #94a3af;
}

.font-size-13 {
  font-size: 13px !important;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .page-header-wrap {
    margin-bottom: 12px !important;
  }
  .page-main-title {
    font-size: 22px !important;
  }
  .page-subtitle {
    font-size: 12.5px !important;
  }
  .header-actions-group {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px !important;
  }
  .btn-new-purchase,
  .btn-action-outline {
    height: 34px !important;
    font-size: 12px !important;
    padding: 0 10px !important;
    flex: 1 1 auto;
  }
  .btn-icon-only {
    flex: 0 0 34px !important;
    width: 34px !important;
    min-width: 34px !important;
  }

  .filter-card-bar {
    margin-bottom: 12px !important;
  }
  .filter-row {
    gap: 8px;
  }
  .date-item {
    flex: 1 1 calc(50% - 4px) !important;
    width: calc(50% - 4px) !important;
    min-width: 130px !important;
    margin-right: 0 !important;
  }
  .search-item {
    flex: 1 1 100% !important;
    width: 100% !important;
    min-width: 100% !important;
    margin-right: 0 !important;
  }
  .status-item {
    flex: 1 1 calc(40% - 4px) !important;
    width: auto !important;
    min-width: 110px !important;
    margin-right: 0 !important;
  }
  .btn-search-amber {
    flex: 1 1 auto !important;
    height: 38px !important;
    margin-right: 0 !important;
    padding: 0 14px !important;
  }
  .btn-reset-outline {
    flex: 0 0 auto !important;
    height: 38px !important;
    padding: 0 12px !important;
  }

  .custom-table th,
  .custom-table td {
    padding: 10px 10px !important;
    font-size: 12px !important;
  }
  .table-footer {
    flex-direction: column !important;
    align-items: center !important;
    gap: 12px !important;
    text-align: center !important;
    padding: 12px 8px !important;
  }
  .footer-pagination-wrap {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
